import React from 'react';
import { Form, Formik, FormikHelpers, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import * as authService from '../../api/authService';
import useInitContext from '../../hooks/useInitContext';
import useAuthContext from '../../hooks/useAuthContext';
import useNavContext from '../../hooks/useNavContext';
import useDeepLinkContext from '../../hooks/useDeepLinkContext';
import Flexlogo from '../../assets/images/logo-red.png';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(5),
    },
  },
  form: {
    marginTop: theme.spacing(4),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(),
    justifyContent: 'center',
  },
  forgetContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  logoContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 140,
    height: 50,
  },
  merchantLogo: {
    height: 100,
    width: 100,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  noAccountContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orLabel: {
    marginTop: 12,
  },
  selectedTabLiner: {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    width: '80%',
  },
  tabLiner: {
    backgroundColor: theme.palette.common.white,
    height: 3,
    width: '80%',
  },
  tabWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
  },
  pinHeader: {
    textAlign: 'center',
  },
  actionsMobNum: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(),
    justifyContent: 'flex-end',
  },
  headerText: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 25,
  },
  actionButton: {
    marginTop: theme.spacing(2),
    color: '#666666',
  },
}));

interface EmailValues {
  email: string;
  password: string;
}

const ValidationEmailSchema = Yup.object().shape({
  email: Yup.string().required('Required').email('Not a valid email'),
  password: Yup.string()
    .required('Required')
    .min(8, 'Must be greater than 8 characters'),
});

const initialEmailValues: EmailValues = {
  email: '',
  password: '',
};

/**
 * SignIn displays the sign in page.
 */
export default function SignInEmail() {
  const classes = useStyles();
  const authentication = useAuthContext();
  const nav = useNavContext();
  const dl = useDeepLinkContext();
  const init = useInitContext();
  const merchantLogo = dl?.deeplink?.logo;
  const history = useHistory();

  const handleEmailSubmit = async (
    values: EmailValues,
    { setFieldError, setSubmitting }: FormikHelpers<EmailValues>
  ) => {
    const { email, password } = values;

    try {
      const res = await authService.signIn(email.toLowerCase(), password);

      authentication?.setSession({
        id: res.id,
        token: res.token,
        userId: '1',
      });
      init?.setInit({
        isFirstLoginSignup: true,
      });
      nav?.setNav({
        menuNav: 'home',
      });
    } catch (e: any) {
      e.data.forEach((err: any) => {
        setFieldError(err.meta.attribute, err.detail);
      });

      setSubmitting(false);
    }
  };

  return (
    <div>
      <Grid container>
        <Grid item xs={12}>
          <div className={classes.content}>
            <div className={classes.logoContainer}>
              <img
                src={merchantLogo ? merchantLogo : Flexlogo}
                className={merchantLogo ? classes.merchantLogo : classes.logo}
                alt="flex reward"
              />
            </div>
            <div className={classes.header}>
              <Typography className={classes.headerText}>
                Log In with Email
              </Typography>
            </div>
            <Formik
              initialValues={initialEmailValues}
              validationSchema={ValidationEmailSchema}
              onSubmit={handleEmailSubmit}
            >
              {({ isSubmitting, isValid }) => (
                <Form noValidate className={classes.form}>
                  <Field
                    component={TextField}
                    className={classes.textField}
                    name="email"
                    type="email"
                    fullWidth
                    variant="standard"
                    placeholder="Email"
                  />

                  <Field
                    component={TextField}
                    className={classes.textField}
                    name="password"
                    type="password"
                    fullWidth
                    variant="standard"
                    placeholder="Password"
                  />
                  <div className={classes.forgetContainer}>
                    <Typography variant="body2">
                      <Link component={RouterLink} to="/forgot_password">
                        Forgot Password?
                      </Link>
                    </Typography>
                  </div>
                  <div className={classes.actions}>
                    <Button
                      type="submit"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={isSubmitting || !isValid}
                    >
                      LOG IN
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      className={classes.actionButton}
                      fullWidth
                      size="large"
                      onClick={() => history.goBack()}
                    >
                      BACK
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Grid>
      </Grid>
      <div id="recaptcha-container"></div>
    </div>
  );
}
